import React, { useState } from 'react';
import {
  ApiButton,
  Box,
  Button,
  ContentTemplate,
  ErrorInfo,
  Grid,
  Progress,
  SectionHeader,
} from 'web-library';
import { useTranslation } from 'react-i18next';
import { Theme as AugmentedTheme } from '@mui/material/styles';

import { makeStyles } from 'tss-react/mui';
import { useHistory, useParams } from 'react-router-dom';
import { useMediaQuery, useTheme } from '@mui/material';
import RouteKey from '../../../routes/key';

import PersonalDetails from '../../examRegistration/components/particulars/PersonalDetails';
import ContactDetails from '../../examRegistration/components/particulars/ContactDetails';
import { useGetReviewExamAmendment } from '../../../api/candidate/exam/@candidate/queries-services/queries';
import ExamDraftMessage from '../../examRegistration/components/ExamDraftMessage';
import { useUpdateRegistrationParticulars } from '../../examRegistration/logic/useUpdateRegistrationParticulars';
import { GET_REVIEW_EXAM_AMENDMENT } from '../../../api/candidate/exam/@candidate/queries-services/keys';
import { CandidateProfileFormData } from '../../../models';
import NextOfKinContactDetails from '../../examRegistration/components/particulars/NextOfKinContactDetails';
import ParticularsEditButtons from '../../examRegistration/components/particulars/ParticularsEditButtons';
import ParticularsSaveButtons from '../../examRegistration/components/particulars/ParticularsSaveButtons';

const { amendment } = RouteKey.child;
const {
  examRegistrations,
  confirmAmendmentDetails,
  coordinatorExamRegistrations,
  coordinatorConfirmAmendmentDetails,
} = RouteKey.amendment.child;

const useStyles = makeStyles()((theme) =>
({
  actionContainer: {
    display: 'flex',
    flexDirection: 'row',
    width: '100%',
    marginTop: theme.spacing(2),
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column',
    },
  },
  backButton: {
    marginRight: theme.spacing(2),
    [theme.breakpoints.down('sm')]: {
      marginRight: theme.spacing(1),
    },
  },
  proceedToSummaryButton: {
    [theme.breakpoints.down('sm')]: {
      height: 'fit-content',
    },
  },
}));

interface ReviewParticularsPathParams {
  uuid?: string;
}

const ReviewParticulars = ({ isGroup }: { isGroup: boolean }) => {
  const { classes } = useStyles();
  const { t } = useTranslation();
  const history = useHistory();
  const { uuid = '' } = useParams<ReviewParticularsPathParams>();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [errorMsg, setErrorMsg] = useState<string>('');
  const [errorNonEditMsg, setErrorNonEditMsg] = useState<string>('');

  const { data } = useGetReviewExamAmendment(uuid);
  const updateParticularHook = useUpdateRegistrationParticulars({
    particularDetails: data?.candidateDetails,
    examUuid: uuid,
    queryKeyToInvalidateAfterUpdate: GET_REVIEW_EXAM_AMENDMENT,
    setErrorMsg,
    setReviewParticularErrorMessage: setErrorNonEditMsg,
  });

  const proceedUrl: string = isGroup
    ? `/${amendment}/${coordinatorExamRegistrations.route}/${coordinatorConfirmAmendmentDetails.route}/${uuid}`
    : `/${amendment}/${examRegistrations.route}/${uuid}/${confirmAmendmentDetails.route}`;

  const isIPsle =
    data?.groupedLevelFees?.length &&
    data?.groupedLevelFees.filter((item) => item.examLevel === 'iPSLE').length;

  if (updateParticularHook.isSettingUpFormValue || !data) {
    return (
      <ContentTemplate>
        <Progress color="secondary" />
      </ContentTemplate>
    );
  }
  const proceedToSummary = () => {
    if (!data.candidateDetails?.localAddressPreferred) {
      setErrorNonEditMsg('Preferred Address must be a Singapore Address');
    } else {
      setErrorNonEditMsg('');
      history.push(proceedUrl);
    }
  };

  return (
    <>
      <ContentTemplate type="full">
        <form
          onSubmit={updateParticularHook.form.handleSubmit((formData: CandidateProfileFormData) => {
            setErrorMsg('');
            updateParticularHook.onSubmitHandler(formData);
          })}
        >
          <SectionHeader
            title={t('amendment->>ReviewParticulars.Review Particulars')}
            componentDisplayBlock={isMobile && updateParticularHook.isEditState}
          >
            {
                !isIPsle &&
                !updateParticularHook.isEditState && (
                    <ParticularsEditButtons {...updateParticularHook} />
                )
            }
          </SectionHeader>
          <PersonalDetails examCandidateDetail={data.candidateDetails} {...updateParticularHook} />
          <ContactDetails contactDetails={data.candidateDetails} {...updateParticularHook} />
          <NextOfKinContactDetails
            {...data.candidateDetails}
            accordionBottomLine={false}
            {...updateParticularHook}
            isLastItem
          />
          {updateParticularHook.isEditState && (
            <>
              <ErrorInfo label={errorMsg} />
              <ParticularsSaveButtons {...updateParticularHook} />
            </>
          )}
        </form>
        <Box className={classes.actionContainer}>
          {!updateParticularHook.isEditState && <ErrorInfo label={errorNonEditMsg || ''} />}
        </Box>
        <Grid container className={classes.actionContainer} xs={12} md={12} spacing={3} >
            <Grid xs={12} md={3}>
              <Button
                fullWidth
                className={classes.backButton}
                variant="outlined"
                onClick={() => history.goBack()}
              >
                {t('amendment->>ReviewParticulars.Back')}
              </Button>
            </Grid>
            <Grid xs={12} md={4}>
              <ApiButton
                className={classes.proceedToSummaryButton}
                id="button-confirmParticulars"
                fullWidth
                status="idle"
                type="submit"
                disabled={updateParticularHook.isEditState}
                onClick={proceedToSummary}
              >
                {t('amendment->>ReviewParticulars.Proceed to Summary')}
              </ApiButton>
            </Grid>
        </Grid>
        <ExamDraftMessage />
      </ContentTemplate>
    </>
  );
};

export default ReviewParticulars;
